import { Expose, Transform } from 'class-transformer';
import { Point } from 'geojson';

import { SnModel } from '@lib/common/models';

import { IFsLprResult } from '../interfaces/common/result';
import { FsLprAxonVIAgencyInfo } from './lpr-axon-vi-agency-info';

// This model class is used to represent the data returned from the Fleet LPR API.
export class FsLprFleetRead extends SnModel implements IFsLprResult {
  @Expose()
  public id: string;

  // name of the camera
  @Expose()
  public name: string;

  // owner of the camera
  @Expose()
  public owner: FsLprFleetReadOwner;

  @Expose()
  public plate: string;

  @Expose()
  public plateImageUrl: string;

  @Expose()
  public contextImageUrl: string;

  @Expose()
  @Transform((value) => {
    // This should be handled on the backend; Remove this @Transform if/when that's done.
    if (value?.color?.name?.toLowerCase() === 'unknown') {
      return {
        ...value,
        color: {
          ...value.color,
          name: '',
        },
      };
    }

    return value;
  })
  public vehicleProperties: FsLprFleetReadVehicleProperties;

  /**
   * sortValues is passed a query parameter to the API in order to start the search from a specific value
   * Take the last value in the previous result set and use that to start the next search (essentially pagination)
   */
  @Expose()
  public sortValues: [number, string];

  @Expose()
  public type: string;

  // deprecated
  @Expose()
  public confidence: string;

  @Expose()
  public organization: string;

  @Expose()
  public isHit: boolean;

  @Expose()
  public hitReason: string;

  @Expose()
  public geometry: Point;

  @Expose()
  @Transform((value) => {
    // This should be handled on the backend; Remove this @Transform if/when that's done.
    if (value?.toLowerCase() === 'unknown') {
      return '';
    }
    return value;
  })
  public state: string;

  @Expose()
  public stateConfidence: number;

  @Expose()
  public subdivisionCode: string;

  @Expose()
  public createdTimestamp: Date;

  @Expose()
  public overviewImageKey: string;

  @Expose()
  public plateImageKey: string;

  @Expose()
  public integratedLprType?: string;

  @Expose()
  public imageCoordinates: plateCoords;

  @Expose()
  public plateBoundingBox: plateCoords;

  @Expose()
  public hotlistInfo: HotlistInfo;

  @Expose()
  public isSharedRecord: boolean;

  @Expose()
  public agencyInfo: FsLprAxonVIAgencyInfo;

  // cropped context image url that includes the plate
  public croppedContextImageUrl: string;

  get cameraName(): string {
    return this.name || '';
  }

  get vehicleColor(): string {
    return this.vehicleProperties?.color?.name || '';
  }

  get vehicleColorConfidence(): number {
    return this.vehicleProperties?.color?.confidence || 0;
  }

  get vehicleMake(): string {
    return this.vehicleProperties?.make?.name || '';
  }

  get vehicleMakeConfidence(): number {
    return this.vehicleProperties?.make?.confidence || 0;
  }

  get vehicleBody(): string {
    return this.vehicleProperties?.body?.name || '';
  }

  get vehicleBodyConfidence(): number {
    return this.vehicleProperties?.body?.confidence || 0;
  }

  get displayState(): string {
    return this.subdivisionCode || this.state;
  }

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }
}

export interface FsLprFleetReadVehicleProperties {
  color: {
    name: string;
    confidence: number;
  };
  make: {
    name: string;
    confidence: number;
  };
  body: {
    name: string;
    confidence: number;
  };
}

export interface FsLprFleetReadOwner {
  firstName: string;
  lastName: string;
  id: string;
  badgeNumber: string;
}

export interface plateCoords {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface HotlistOwnerInfo {
  firstName: string;
  lastName: string;
  gender: string;
  address1: string;
  address2: string;
  country: string;
  city: string;
  state: string;
  zip: string;
  weight: string;
  height: string;
  eyes: string;
  birthdate: string;
}

export interface HotlistCarInfo {
  vehicleType: string;
  year: string;
  model: string;
  make: string;
  vin: string;
  color: string;
  tabExpirationDate: string;
  registrationDate: string;
}

export interface HotlistMiscInfo {
  additionalProp1: string | null;
  additionalProp2: string | null;
  additionalProp3: string | null;
}

export interface HotlistInfo {
  id: string;
  listName: string;
  licensePlate: string;
  offenseCategoryId: number;
  offensePriority: number;
  offenseCategoryName: string;
  offensePriorityText: string;
  recordExpiration: string;
  registrationState: string;
  registrationCountry: string;
  ownerInfo: HotlistOwnerInfo;
  carInfo: HotlistCarInfo;
  miscInfo: HotlistMiscInfo;
  note: string;
  createdDate: string;
  modifiedDate: string;
}
